import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

function HomeNavbar() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);

    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled 
        ? 'bg-[#0a0e27]/98 backdrop-blur-xl shadow-xl border-b border-blue-500/40' 
        : 'bg-[#0a0e27]/95 backdrop-blur-md border-b border-blue-500/30'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-18 lg:h-20">
          {/* Brand logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 hover:opacity-90 transition-opacity"
            aria-label="ConvrsAI home"
          >
            <img
              src="/logo.png"
              alt="Convrs AI - Verified Leads, Proven Engagement"
              className="h-8 sm:h-9 md:h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation  */}
          
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            <a
              href="/privacy-policy"
              className="text-gray-300 hover:text-blue-400 transition-colors text-sm font-medium"
            >
              Privacy Policy
            </a>
            <a
              href="/terms"
              className="text-gray-300 hover:text-blue-400 transition-colors text-sm font-medium"
            >
              Terms
            </a>
            {isLoggedIn ? (
              <>
                <button
                  onClick={() => navigate('/tasks')}
                  className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
                >
                  Tasks
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-5 py-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-lg transition-all shadow-lg shadow-blue-600/30"
                >
                  Dashboard
                </button>
              </>
            ) : (
              <>
                <a
                  href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ1LRerQzILalOQbfT9l5lWQ6abwrkk6n45IbqR_cPsf8tH09a8kVlButSqrKMTkMo1De-h8vUdB"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 py-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-lg transition-all shadow-lg shadow-blue-600/30 whitespace-nowrap"
                >
                  Book a Demo
                </a>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate('/login')}
                    className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => navigate('/signup')}
                    className="px-5 py-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-lg transition-all shadow-lg shadow-blue-600/30"
                  >
                    Get Started
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-300 hover:text-white focus:outline-none p-2"
              aria-label="Toggle menu"
            >
              <svg className="h-6 w-6" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                {mobileMenuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-blue-500/20 py-4 space-y-1">
            <div className="px-4 pt-2 space-y-2 border-t border-gray-700 mt-2">
              <a
                href="/privacy-policy"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-2.5 text-gray-300 hover:text-blue-400 hover:bg-blue-600/10 transition-colors text-sm font-medium rounded-lg"
              >
                Privacy Policy
              </a>
              <a
                href="/terms"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-2.5 text-gray-300 hover:text-blue-400 hover:bg-blue-600/10 transition-colors text-sm font-medium rounded-lg"
              >
                Terms & Conditions
              </a>
              {isLoggedIn ? (
                <>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate('/tasks');
                    }}
                    className="w-full px-4 py-2.5 text-sm font-medium text-gray-300 hover:text-white hover:bg-blue-600/10 transition-colors text-left rounded-lg"
                  >
                    Tasks
                  </button>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate('/dashboard');
                    }}
                    className="w-full px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-lg transition-all shadow-lg"
                  >
                    Dashboard
                  </button>
                </>
              ) : (
                <>
                  <a
                    href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ1LRerQzILalOQbfT9l5lWQ6abwrkk6n45IbqR_cPsf8tH09a8kVlButSqrKMTkMo1De-h8vUdB"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileMenuOpen(false)}
                    className="w-full px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-lg transition-all shadow-lg text-center block"
                  >
                    Book a Demo
                  </a>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate('/login');
                    }}
                    className="w-full px-4 py-2.5 text-sm font-medium text-gray-300 hover:text-white hover:bg-blue-600/10 transition-colors text-left rounded-lg"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate('/signup');
                    }}
                    className="w-full px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-lg transition-all shadow-lg"
                  >
                    Get Started
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

export default HomeNavbar;
